import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity,
  ArrowUpRight,
  CircleCheck,
  CreditCard,
  Download,
  Gauge,
  ReceiptText,
  type LucideIcon,
} from 'lucide-react';
import { useApiClient, PageLayout } from '@flowlib/ui';
import {
  type BillingCycle,
  type BillingInvoiceSummary,
  type BillingSummary,
  type CurrentPlan,
  type PlanId,
  type PlanWithPrices,
  type Usage,
  getBillingSummary,
  getCurrentPlan,
  getUsage,
  listPlans,
  openPortal,
  startCheckout,
} from './billing-api';

const CardIcon = CreditCard as unknown as LucideIcon;
const SALES_EMAIL = 'accounts@flowlib.dev';

const BILLING_TABS = [
  { key: 'usage', label: 'Usage' },
  { key: 'plan', label: 'Plan' },
  { key: 'payment-history', label: 'Payment History' },
] as const;

type BillingTabKey = (typeof BILLING_TABS)[number]['key'];

const EMPTY_BILLING_SUMMARY: BillingSummary = {
  stripeConfigured: false,
  customerEmail: null,
  defaultPaymentMethod: null,
  invoices: [],
};

type LoadState =
  | { kind: 'loading' }
  | {
      kind: 'ready';
      current: CurrentPlan;
      plans: PlanWithPrices[];
      usage: Usage;
      summary: BillingSummary;
    }
  | { kind: 'error'; message: string };

export function BillingPage() {
  const api = useApiClient();
  const apiBase = api.getBaseURL();

  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [tick, setTick] = useState(0);
  const [cycle, setCycle] = useState<BillingCycle>('monthly');
  const [activeTab, setActiveTab] = useState<BillingTabKey>('usage');
  const [pendingPlan, setPendingPlan] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState<{ expected: string } | null>(null);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    void (async () => {
      try {
        const [current, plans, usage, summary] = await Promise.all([
          getCurrentPlan(apiBase),
          listPlans(apiBase),
          getUsage(apiBase),
          getBillingSummary(apiBase).catch(() => EMPTY_BILLING_SUMMARY),
        ]);
        if (cancelled) {
          return;
        }
        setCycle(current.billingCycle ?? 'monthly');
        setState({ kind: 'ready', current, plans, usage, summary });
      } catch (err) {
        if (cancelled) {
          return;
        }
        setState({
          kind: 'error',
          message: err instanceof Error ? err.message : 'Failed to load billing',
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiBase, tick]);

  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get('stripe') !== 'success') {
      return;
    }
    const expected = url.searchParams.get('plan') ?? 'pro';
    setVerifying({ expected });
    url.searchParams.delete('stripe');
    url.searchParams.delete('plan');
    window.history.replaceState({}, '', url.toString());

    let cancelled = false;
    const start = Date.now();
    const poll = async () => {
      while (!cancelled && Date.now() - start < 30_000) {
        try {
          const current = await getCurrentPlan(apiBase);
          if (current.plan === expected) {
            if (!cancelled) {
              setVerifying(null);
              refresh();
            }
            return;
          }
        } catch {
          // Keep polling while Stripe's webhook catches up.
        }
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
      if (!cancelled) {
        setVerifying(null);
        refresh();
      }
    };
    void poll();
    return () => {
      cancelled = true;
    };
  }, [apiBase, refresh]);

  const onCheckout = useCallback(
    async (planId: 'pro') => {
      setActionError(null);
      setPendingPlan(planId);
      try {
        const returnUrl = `${window.location.origin}/settings/billing?plan=${planId}`;
        const { url } = await startCheckout(apiBase, { planId, cycle, returnUrl });
        window.location.assign(url);
      } catch (err) {
        setActionError(err instanceof Error ? err.message : 'Checkout failed');
        setPendingPlan(null);
      }
    },
    [apiBase, cycle],
  );

  const onPortal = useCallback(async () => {
    setActionError(null);
    setPendingPlan('portal');
    try {
      const returnUrl = `${window.location.origin}/settings/billing`;
      const { url } = await openPortal(apiBase, { returnUrl });
      window.location.assign(url);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Could not open billing portal');
      setPendingPlan(null);
    }
  }, [apiBase]);

  if (state.kind === 'loading') {
    return (
      <PageLayout
        title="Billing"
        subtitle="Manage your subscription and usage."
        icon={CardIcon as never}
      >
        <BillingSkeleton />
      </PageLayout>
    );
  }

  if (state.kind === 'error') {
    return (
      <PageLayout
        title="Billing"
        subtitle="Manage your subscription and usage."
        icon={CardIcon as never}
      >
        <div className="rounded-md border border-fl-destructive/30 bg-fl-destructive/10 p-3 text-sm text-fl-destructive">
          {state.message}
        </div>
      </PageLayout>
    );
  }

  const { current, plans, usage, summary } = state;

  return (
    <PageLayout
      title="Billing"
      subtitle="Manage your subscription and usage."
      icon={CardIcon as never}
    >
      <div className="flex flex-col gap-8">
        <BillingTabs tabs={BILLING_TABS} active={activeTab} onChange={setActiveTab} />

        {verifying && <Notice>Verifying your subscription...</Notice>}
        {actionError && (
          <div className="rounded-md border border-fl-destructive/30 bg-fl-destructive/10 p-3 text-sm text-fl-destructive">
            {actionError}
          </div>
        )}

        <TabPanel active={activeTab === 'usage'}>
          <UsageTab usage={usage} caps={current.caps} />
        </TabPanel>

        <TabPanel active={activeTab === 'plan'}>
          <PlanTab
            current={current}
            plans={plans}
            cycle={cycle}
            summary={summary}
            onCycleChange={setCycle}
            onCheckout={onCheckout}
            onPortal={onPortal}
            pendingPlan={pendingPlan}
          />
        </TabPanel>

        <TabPanel active={activeTab === 'payment-history'}>
          <PaymentHistoryTab
            summary={summary}
            current={current}
            onPortal={onPortal}
            portalBusy={pendingPlan === 'portal'}
          />
        </TabPanel>
      </div>
    </PageLayout>
  );
}

function BillingTabs<TKey extends string>({
  tabs,
  active,
  onChange,
}: {
  tabs: ReadonlyArray<{ key: TKey; label: string }>;
  active: TKey;
  onChange: (key: TKey) => void;
}) {
  return (
    <div
      role="tablist"
      className="flex items-center gap-2 overflow-x-auto border-b border-fl-border"
    >
      {tabs.map((tab) => {
        const isActive = tab.key === active;
        return (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(tab.key)}
            className={
              isActive
                ? '-mb-px shrink-0 border-b-2 border-fl-foreground px-3 py-2.5 text-sm font-medium text-fl-foreground transition-colors'
                : '-mb-px shrink-0 border-b-2 border-transparent px-3 py-2.5 text-sm font-medium text-fl-muted-foreground transition-colors hover:text-fl-foreground'
            }
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

function TabPanel({ active, children }: { active: boolean; children: ReactNode }) {
  if (!active) {
    return null;
  }
  return <div role="tabpanel">{children}</div>;
}

function SettingsRow({
  title,
  description,
  children,
}: {
  title: string;
  description?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-10">
      <div className="md:col-span-1">
        <h2 className="text-sm font-semibold leading-tight text-fl-foreground">{title}</h2>
        {description && <p className="mt-1 text-sm text-fl-muted-foreground">{description}</p>}
      </div>
      <div className="md:col-span-2">{children}</div>
    </div>
  );
}

function SettingsDivider() {
  return <hr className="border-t border-fl-border" />;
}

function UsageTab({ usage, caps }: { usage: Usage; caps: CurrentPlan['caps'] }) {
  return (
    <div className="flex flex-col gap-8">
      <SettingsRow
        title="Usage this period"
        description="Track current activity against the limits included with your plan."
      >
        <UsageSection usage={usage} caps={caps} />
      </SettingsRow>
    </div>
  );
}

function PlanTab({
  current,
  plans,
  cycle,
  summary,
  onCycleChange,
  onCheckout,
  onPortal,
  pendingPlan,
}: {
  current: CurrentPlan;
  plans: PlanWithPrices[];
  cycle: BillingCycle;
  summary: BillingSummary;
  onCycleChange: (cycle: BillingCycle) => void;
  onCheckout: (planId: 'pro') => void;
  onPortal: () => void;
  pendingPlan: string | null;
}) {
  return (
    <div className="flex flex-col gap-8">
      <SettingsRow
        title="Current plan"
        description="Your active subscription, renewal timing, and billing cycle."
      >
        <PlanSummary current={current} plans={plans} cycle={cycle} summary={summary} />
      </SettingsRow>

      <SettingsDivider />

      <SettingsRow
        title="Manage plan"
        description="Upgrade to Pro, change billing cadence, or open Stripe to manage an existing plan."
      >
        <BillingActionBar
          current={current}
          plans={plans}
          cycle={cycle}
          onCycleChange={onCycleChange}
          onCheckout={onCheckout}
          onPortal={onPortal}
          pendingPlan={pendingPlan}
        />
      </SettingsRow>
    </div>
  );
}

function PaymentHistoryTab({
  summary,
  current,
  onPortal,
  portalBusy,
}: {
  summary: BillingSummary;
  current: CurrentPlan;
  onPortal: () => void;
  portalBusy: boolean;
}) {
  return (
    <div className="flex flex-col gap-8">
      <SettingsRow
        title="Payment method"
        description="Default payment details are stored and managed securely by Stripe."
      >
        <PaymentMethodSection
          summary={summary}
          current={current}
          onPortal={onPortal}
          portalBusy={portalBusy}
        />
      </SettingsRow>

      <SettingsDivider />

      <SettingsRow
        title="Payment history"
        description="Recent invoices and downloadable receipts for this workspace."
      >
        <RecentInvoicesSection invoices={summary.invoices} onPortal={onPortal} />
      </SettingsRow>
    </div>
  );
}

function BillingSkeleton() {
  return (
    <div className="flex flex-col gap-8">
      <div className="flex items-center gap-2 border-b border-fl-border">
        <div className="h-10 w-16 rounded-t bg-fl-muted" />
        <div className="h-10 w-14 rounded-t bg-fl-muted" />
        <div className="h-10 w-28 rounded-t bg-fl-muted" />
      </div>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-10">
        <div className="space-y-2">
          <div className="h-4 w-32 rounded bg-fl-muted" />
          <div className="h-4 w-44 rounded bg-fl-muted" />
        </div>
        <div className="grid gap-3 sm:grid-cols-3 md:col-span-2">
          <div className="h-20 rounded-md border border-fl-border bg-fl-muted" />
          <div className="h-20 rounded-md border border-fl-border bg-fl-muted" />
          <div className="h-20 rounded-md border border-fl-border bg-fl-muted" />
        </div>
      </div>
    </div>
  );
}

function Notice({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-md border border-fl-border bg-fl-muted/30 p-3 text-sm text-fl-muted-foreground">
      {children}
    </div>
  );
}

function PlanSummary({
  current,
  plans,
  cycle,
  summary,
}: {
  current: CurrentPlan;
  plans: PlanWithPrices[];
  cycle: BillingCycle;
  summary: BillingSummary;
}) {
  const activeCycle = current.billingCycle ?? cycle;
  const price = getPlanPrice(plans, current.plan, activeCycle);
  const amountLabel = getPlanAmountLabel(current, plans, activeCycle);
  const annualEstimate = price
    ? formatCurrency(
        activeCycle === 'annual' ? price.unitAmount : price.unitAmount * 12,
        price.currency,
      )
    : null;
  const renewalLabel = current.currentPeriodEnd
    ? `${current.cancelAtPeriodEnd ? 'Ends' : 'Renews'} ${formatDate(current.currentPeriodEnd)}`
    : current.plan === 'free'
      ? 'Free plan'
      : 'Renewal date unavailable';

  return (
    <div className="rounded-md border border-fl-border bg-fl-card p-4 shadow-sm">
      <div className="mb-3 flex items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-sm font-medium text-fl-foreground">{current.planName}</h2>
            <StatusPill status={current.status} />
          </div>
          <p className="mt-0.5 text-xs text-fl-muted-foreground">{renewalLabel}</p>
        </div>
        <div className="shrink-0 text-right">
          <div className="text-2xl font-semibold tracking-normal text-fl-foreground tabular-nums">
            {amountLabel}
            {price && (
              <span className="ml-0.5 text-xs font-normal text-fl-muted-foreground">
                /{activeCycle === 'annual' ? 'yr' : 'mo'}
              </span>
            )}
          </div>
        </div>
      </div>

      <dl className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-fl-muted-foreground">
        <SummaryTerm
          label="Next charge"
          value={
            price
              ? formatCurrency(price.unitAmount, price.currency)
              : current.plan === 'free'
                ? '$0.00'
                : 'Portal'
          }
        />
        {summary.invoices.length > 0 && (
          <SummaryTerm
            label="YTD spend"
            value={formatCurrency(sumInvoiceSpend(summary.invoices), summary.invoices[0]?.currency)}
          />
        )}
        <SummaryTerm label="Billing cycle" value={labelize(activeCycle)} />
        <SummaryTerm label="Est. annual" value={annualEstimate ?? 'Custom'} />
      </dl>
    </div>
  );
}

function SummaryTerm({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-fl-muted-foreground">{label}:</dt>
      <dd className="ml-1 inline font-medium text-fl-foreground tabular-nums">{value}</dd>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const tone =
    status === 'active'
      ? 'border-emerald-500/25 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
      : status === 'past_due'
        ? 'border-amber-500/25 bg-amber-500/10 text-amber-600 dark:text-amber-400'
        : 'border-fl-destructive/25 bg-fl-destructive/10 text-fl-destructive';
  return (
    <span
      className={`inline-flex h-4 items-center gap-1 rounded-full border px-1.5 text-[10px] font-medium ${tone}`}
    >
      {status === 'active' ? (
        <CircleCheck className="size-2.5" />
      ) : (
        <span className="size-1.5 rounded-full bg-current" />
      )}
      {labelize(status)}
    </span>
  );
}

const METRIC_META: Record<string, { label: string; icon: LucideIcon }> = {
  flowRuns: { label: 'Flow Runs', icon: Activity },
  agentToolInvocations: { label: 'Tool Calls', icon: Gauge },
  llmTokens: { label: 'LLM Tokens', icon: Gauge },
};

function UsageSection({ usage, caps }: { usage: Usage; caps: CurrentPlan['caps'] }) {
  const rows = useMemo(() => {
    const keys = new Set([...Object.keys(caps), ...Object.keys(usage)]);
    return Array.from(keys).map((metric) => ({
      metric,
      used: usage[metric] ?? 0,
      cap: caps[metric as keyof CurrentPlan['caps']] ?? 0,
    }));
  }, [usage, caps]);

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      {rows.map((row) => (
        <UsageBar
          key={row.metric}
          label={METRIC_META[row.metric]?.label ?? labelize(row.metric)}
          icon={METRIC_META[row.metric]?.icon ?? Gauge}
          used={row.used}
          cap={row.cap}
        />
      ))}
    </div>
  );
}

function UsageBar({
  label,
  icon: Icon,
  used,
  cap,
}: {
  label: string;
  icon: LucideIcon;
  used: number;
  cap: number;
}) {
  const unlimited = cap === 0;
  const pct = unlimited ? 0 : Math.min(100, Math.round((used / Math.max(1, cap)) * 100));
  const over = !unlimited && used >= cap;
  return (
    <div className="min-w-0 space-y-1.5 rounded-md border border-fl-border bg-fl-card p-3 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-1.5 font-medium text-fl-foreground">
          <Icon className="size-3 shrink-0 text-fl-muted-foreground" />
          <span className="truncate text-xs">{label}</span>
        </div>
        <span
          className={
            over
              ? 'text-[10px] font-medium text-amber-600 tabular-nums'
              : 'text-[10px] text-fl-muted-foreground tabular-nums'
          }
        >
          {unlimited ? 'Open' : `${pct}%`}
        </span>
      </div>
      <div className="h-1 w-full overflow-hidden rounded-full bg-fl-primary/20">
        <div
          className={over ? 'h-full bg-amber-600' : 'h-full bg-fl-primary'}
          style={{ width: unlimited ? '100%' : `${pct}%` }}
        />
      </div>
      <p className="text-[10px] text-fl-muted-foreground tabular-nums">
        {formatCompactMetric(used)} of {unlimited ? 'unlimited' : formatCompactMetric(cap)}
      </p>
    </div>
  );
}

function PaymentMethodSection({
  summary,
  current,
  onPortal,
  portalBusy,
}: {
  summary: BillingSummary;
  current: CurrentPlan;
  onPortal: () => void;
  portalBusy: boolean;
}) {
  const method = summary.defaultPaymentMethod;
  const canOpenPortal = current.plan !== 'free' || Boolean(method);
  return (
    <div className="rounded-md border border-fl-border bg-fl-card p-4 shadow-sm">
      <div className="flex items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3">
          <CreditCard className="size-4 shrink-0 text-fl-muted-foreground" />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-sm font-medium text-fl-foreground">
                {method
                  ? `${labelize(method.brand ?? 'Card')} ending in ${method.last4 ?? '----'}`
                  : 'Payment method'}
              </h3>
              {method && (
                <span className="inline-flex h-4 items-center rounded-full bg-fl-muted px-1.5 text-[10px] font-medium text-fl-muted-foreground">
                  Default
                </span>
              )}
            </div>
            <p className="mt-0.5 text-xs text-fl-muted-foreground">
              {method
                ? 'Managed securely by Stripe'
                : summary.stripeConfigured
                  ? 'Add or update payment details in Stripe.'
                  : 'Stripe billing is not configured in this environment.'}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onPortal}
          disabled={!canOpenPortal || portalBusy}
          className="h-7 shrink-0 rounded-md px-3 text-xs font-medium text-fl-foreground hover:bg-fl-muted disabled:cursor-not-allowed disabled:opacity-50"
        >
          {portalBusy ? 'Opening...' : method ? 'Change' : 'Open portal'}
        </button>
      </div>
    </div>
  );
}

function RecentInvoicesSection({
  invoices,
  onPortal,
}: {
  invoices: BillingInvoiceSummary[];
  onPortal: () => void;
}) {
  return (
    <div>
      <div className="mb-2.5 flex items-center justify-between gap-4">
        <span className="text-xs font-medium text-fl-muted-foreground">Recent invoices</span>
        <button
          type="button"
          onClick={onPortal}
          className="inline-flex h-6 items-center gap-1 rounded-md px-1.5 text-[10px] text-fl-muted-foreground hover:bg-fl-muted hover:text-fl-foreground"
        >
          View all
          <ArrowUpRight className="size-2.5" />
        </button>
      </div>

      <div className="overflow-hidden rounded-md border border-fl-border">
        <div className="relative w-full overflow-x-auto">
          <table className="w-full caption-bottom text-sm">
            <thead>
              <tr className="border-b border-fl-border bg-fl-muted/50 text-left text-fl-foreground">
                <th className="h-10 px-2 text-xs font-medium whitespace-nowrap">Invoice</th>
                <th className="h-10 px-2 text-xs font-medium whitespace-nowrap">Date</th>
                <th className="h-10 px-2 text-xs font-medium whitespace-nowrap">Amount</th>
                <th className="h-10 px-2 text-xs font-medium whitespace-nowrap">Status</th>
                <th className="h-10 px-2 text-right text-xs font-medium whitespace-nowrap">PDF</th>
              </tr>
            </thead>
            <tbody>
              {invoices.slice(0, 5).map((invoice) => (
                <tr
                  key={invoice.id}
                  className="group border-b border-fl-border transition-colors hover:bg-fl-muted/50 last:border-0"
                >
                  <td className="p-2 align-middle font-mono text-xs whitespace-nowrap">
                    {invoice.id}
                  </td>
                  <td className="p-2 align-middle text-xs text-fl-muted-foreground whitespace-nowrap">
                    {formatDate(invoice.createdAt)}
                  </td>
                  <td className="p-2 align-middle text-sm font-medium whitespace-nowrap tabular-nums">
                    {formatCurrency(invoice.amountPaid ?? invoice.amountDue, invoice.currency)}
                  </td>
                  <td className="p-2 align-middle whitespace-nowrap">
                    <InvoiceStatus status={invoice.status} />
                  </td>
                  <td className="p-2 text-right align-middle whitespace-nowrap">
                    {invoice.invoicePdf ? (
                      <a
                        href={invoice.invoicePdf}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex size-7 items-center justify-center rounded-md text-fl-muted-foreground opacity-50 transition-opacity hover:bg-fl-muted hover:text-fl-foreground group-hover:opacity-100"
                        aria-label={`Download ${invoice.id}`}
                      >
                        <Download className="size-3.5" />
                      </a>
                    ) : (
                      <span className="text-fl-muted-foreground">-</span>
                    )}
                  </td>
                </tr>
              ))}
              {invoices.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-10 text-center text-fl-muted-foreground">
                    <ReceiptText className="mx-auto mb-2 size-5" />
                    No invoices yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function InvoiceStatus({ status }: { status: string | null }) {
  const paid = status === 'paid';
  return (
    <span className="flex items-center gap-1.5">
      <span
        className={
          paid ? 'size-1.5 rounded-full bg-emerald-500' : 'size-1.5 rounded-full bg-amber-500'
        }
      />
      <span className="text-xs text-fl-muted-foreground">{labelize(status ?? 'unknown')}</span>
    </span>
  );
}

function BillingActionBar({
  current,
  plans,
  cycle,
  onCycleChange,
  onCheckout,
  onPortal,
  pendingPlan,
}: {
  current: CurrentPlan;
  plans: PlanWithPrices[];
  cycle: BillingCycle;
  onCycleChange: (cycle: BillingCycle) => void;
  onCheckout: (planId: 'pro') => void;
  onPortal: () => void;
  pendingPlan: string | null;
}) {
  const proPrice = getPlanPrice(plans, 'pro', cycle);
  return (
    <div className="rounded-md border border-fl-border bg-fl-card p-4 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-[10px] text-fl-muted-foreground">
          Billing managed by Stripe. Prices exclude applicable taxes.
        </p>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          {current.plan === 'free' && <CycleToggle cycle={cycle} onChange={onCycleChange} />}
          <PrimaryBillingAction
            current={current}
            proPriceAvailable={Boolean(proPrice)}
            onCheckout={onCheckout}
            onPortal={onPortal}
            pendingPlan={pendingPlan}
          />
        </div>
      </div>
    </div>
  );
}

function CycleToggle({
  cycle,
  onChange,
}: {
  cycle: BillingCycle;
  onChange: (cycle: BillingCycle) => void;
}) {
  return (
    <div className="inline-flex rounded-md border border-fl-border bg-fl-card p-0.5 text-xs">
      {(['monthly', 'annual'] as const).map((value) => (
        <button
          key={value}
          type="button"
          onClick={() => onChange(value)}
          className={
            cycle === value
              ? 'rounded bg-fl-primary px-2.5 py-1 capitalize text-fl-primary-foreground'
              : 'rounded px-2.5 py-1 capitalize text-fl-muted-foreground hover:text-fl-foreground'
          }
        >
          {value}
        </button>
      ))}
    </div>
  );
}

function PrimaryBillingAction({
  current,
  proPriceAvailable,
  onCheckout,
  onPortal,
  pendingPlan,
}: {
  current: CurrentPlan;
  proPriceAvailable: boolean;
  onCheckout: (planId: 'pro') => void;
  onPortal: () => void;
  pendingPlan: string | null;
}) {
  if (current.plan === 'enterprise') {
    return (
      <a
        href={`mailto:${SALES_EMAIL}?subject=${encodeURIComponent('Flowlib Enterprise billing')}`}
        className="inline-flex h-7 items-center justify-center rounded-md bg-fl-primary px-3 text-xs font-medium text-fl-primary-foreground hover:opacity-90"
      >
        Contact Sales
      </a>
    );
  }

  if (current.plan === 'free') {
    return (
      <button
        type="button"
        onClick={() => onCheckout('pro')}
        disabled={pendingPlan === 'pro' || !proPriceAvailable}
        className="inline-flex h-7 items-center justify-center gap-1.5 rounded-md bg-fl-primary px-3 text-xs font-medium text-fl-primary-foreground hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        title={!proPriceAvailable ? 'Pricing unavailable for this cycle' : undefined}
      >
        <ArrowUpRight className="size-3" />
        {pendingPlan === 'pro' ? 'Redirecting...' : 'Upgrade Plan'}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onPortal}
      disabled={pendingPlan === 'portal'}
      className="inline-flex h-7 items-center justify-center gap-1.5 rounded-md bg-fl-primary px-3 text-xs font-medium text-fl-primary-foreground hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
    >
      <ArrowUpRight className="size-3" />
      {pendingPlan === 'portal' ? 'Opening...' : 'Manage Plan'}
    </button>
  );
}

function getPlanPrice(
  plans: PlanWithPrices[],
  planId: PlanId,
  cycle: BillingCycle,
): PlanWithPrices['prices'][number] | null {
  return (
    plans.find((plan) => plan.id === planId)?.prices.find((price) => price.cycle === cycle) ?? null
  );
}

function getPlanAmountLabel(
  current: CurrentPlan,
  plans: PlanWithPrices[],
  cycle: BillingCycle,
): string {
  if (current.plan === 'free') {
    return 'Free';
  }
  if (current.plan === 'enterprise') {
    return 'Custom';
  }
  const price = getPlanPrice(plans, current.plan, cycle);
  return price ? formatCurrency(price.unitAmount, price.currency) : 'Unavailable';
}

function sumInvoiceSpend(invoices: BillingInvoiceSummary[]): number | null {
  if (invoices.length === 0) {
    return null;
  }
  return invoices.reduce((sum, invoice) => sum + (invoice.amountPaid ?? 0), 0);
}

function formatCurrency(
  amountCents: number | null | undefined,
  currency: string | null | undefined,
): string {
  if (amountCents === null || amountCents === undefined || !currency) {
    return '-';
  }
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amountCents / 100);
}

function formatDate(seconds: number | null | undefined): string {
  if (!seconds) {
    return '-';
  }
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(seconds * 1000));
}

function formatCompactMetric(value: number): string {
  return new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(
    value,
  );
}

function labelize(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replaceAll('_', ' ')
    .replace(/^./, (char) => char.toUpperCase());
}
